/**
 * Starfield - Procedural background stars
 * Simplified for R3F compatibility
 */

import { useMemo, useRef } from 'react';
import { useFrame } from '@react-three/fiber';
import * as THREE from 'three';
import { Points } from '@react-three/drei';

interface StarfieldProps {
  count?: number;
  radius?: number;
}

export function Starfield({
  count = 25000,  // Dense star field
  radius = 400    // Far background
}: StarfieldProps) {
  const pointsRef = useRef<THREE.Points>(null);

  const { positions, colors } = useMemo(() => {
    const pos = new Float32Array(count * 3);
    const col = new Float32Array(count * 3);

    for (let i = 0; i < count; i++) {
      // Uniform distribution on sphere - very far back
      const theta = Math.random() * Math.PI * 2;
      const phi = Math.acos(2 * Math.random() - 1);
      const r = radius * (0.9 + Math.random() * 0.1);

      pos[i * 3] = r * Math.sin(phi) * Math.cos(theta);
      pos[i * 3 + 1] = r * Math.sin(phi) * Math.sin(theta);
      pos[i * 3 + 2] = r * Math.cos(phi);

      // Star colors - realistic spectral distribution
      const spectralType = Math.random();
      let r_col: number, g_col: number, b_col: number;
      let brightness: number;

      if (spectralType < 0.02) {
        // Rare bright stars - O/B class blue
        r_col = 0.7; g_col = 0.85; b_col = 1.0;
        brightness = 0.8 + Math.random() * 0.2;
      } else if (spectralType < 0.08) {
        // A class - blue-white
        r_col = 0.9; g_col = 0.95; b_col = 1.0;
        brightness = 0.6 + Math.random() * 0.3;
      } else if (spectralType < 0.35) {
        // F/G class - white/yellow (Sun-like)
        r_col = 1.0; g_col = 1.0; b_col = 0.95;
        brightness = 0.4 + Math.random() * 0.4;
      } else if (spectralType < 0.65) {
        // K class - orange
        r_col = 1.0; g_col = 0.85; b_col = 0.7;
        brightness = 0.3 + Math.random() * 0.3;
      } else {
        // M class - red dwarfs (most common, dimmer)
        r_col = 1.0; g_col = 0.7; b_col = 0.5;
        brightness = 0.15 + Math.random() * 0.25;
      }

      col[i * 3] = r_col * brightness;
      col[i * 3 + 1] = g_col * brightness;
      col[i * 3 + 2] = b_col * brightness;
    }

    return { positions: pos, colors: col };
  }, [count, radius]);

  return (
    <points ref={pointsRef}>
      <bufferGeometry>
        <bufferAttribute
          attach="attributes-position"
          count={count}
          array={positions}
          itemSize={3}
        />
        <bufferAttribute
          attach="attributes-color"
          count={count}
          array={colors}
          itemSize={3}
        />
      </bufferGeometry>
      <pointsMaterial
        size={0.025}
        vertexColors
        transparent
        opacity={1}
        sizeAttenuation
        blending={THREE.AdditiveBlending}
        depthWrite={false}
      />
    </points>
  );
}

// Simplified Milky Way plane
export function MilkyWayPlane() {
  const texture = useMemo(() => {
    const canvas = document.createElement('canvas');
    canvas.width = 512;
    canvas.height = 128;
    const ctx = canvas.getContext('2d')!;

    // Gradient for galactic plane
    const gradient = ctx.createLinearGradient(0, 0, 0, 128);
    gradient.addColorStop(0, 'rgba(100, 120, 150, 0)');
    gradient.addColorStop(0.4, 'rgba(150, 160, 180, 0.1)');
    gradient.addColorStop(0.5, 'rgba(200, 210, 230, 0.15)');
    gradient.addColorStop(0.6, 'rgba(150, 160, 180, 0.1)');
    gradient.addColorStop(1, 'rgba(100, 120, 150, 0)');

    ctx.fillStyle = gradient;
    ctx.fillRect(0, 0, 512, 128);

    // Add some noise
    for (let i = 0; i < 2000; i++) {
      const x = Math.random() * 512;
      const y = 30 + Math.random() * 68;
      const brightness = Math.floor(150 + Math.random() * 105);
      ctx.fillStyle = `rgba(${brightness}, ${brightness + 10}, ${brightness + 20}, ${Math.random() * 0.3})`;
      ctx.fillRect(x, y, 1, 1);
    }

    const tex = new THREE.CanvasTexture(canvas);
    tex.wrapS = THREE.RepeatWrapping;
    tex.repeat.set(4, 1);
    return tex;
  }, []);

  return (
    <mesh rotation={[Math.PI / 2, 0, 0]}>
      <planeGeometry args={[400, 100]} />
      <meshBasicMaterial
        map={texture}
        transparent
        opacity={0.3}
        blending={THREE.AdditiveBlending}
        depthWrite={false}
        side={THREE.DoubleSide}
      />
    </mesh>
  );
}
